import { localized, type SinglePhotoTool } from '@/config/ai-tools';
import { m } from '@/paraglide/messages.js';

/**
 * Studio and page wording for a one-photo trend: its own `copy` override when
 * it has one, otherwise the engine default (image or video) from the message
 * files.
 */
export function resolvePhotoToolCopy(tool: SinglePhotoTool, locale: string) {
  const copy = tool.copy ?? {};
  const video = tool.kind === 'photo-video';
  const pick = (override: Parameters<typeof localized>[0] | undefined, fallback: () => string) =>
    override ? localized(override, locale) : fallback();

  return {
    photoLabel: pick(copy.photoLabel, () => m['directory.ai_tools.photo.photo_label']()),
    photoHint: pick(copy.photoHint, () => m['directory.ai_tools.photo.photo_hint']()),
    stepStyle: pick(copy.stepStyle, () => m['directory.ai_tools.photo.step_style']()),
    stepNote: pick(copy.stepNote, () => m['directory.ai_tools.photo.step_note']()),
    notePlaceholder: pick(copy.notePlaceholder, () => m['directory.ai_tools.photo.note_placeholder']()),
    noteHint: pick(copy.noteHint, () => m['directory.ai_tools.photo.note_hint']()),
    /** Button label for a quality tier costing `credits`. */
    generate: (credits: number) =>
      copy.generate
        ? `${localized(copy.generate, locale)} · ${m['directory.ai_tools.studio.credits']({ credits })}`
        : video
          ? m['directory.ai_tools.photo_video.generate']({ credits })
          : m['directory.ai_tools.photo.generate']({ credits }),
    trustTitle: pick(copy.trustTitle, () =>
      video ? m['directory.ai_tools.photo_video.trust_title']() : m['directory.ai_tools.photo.trust_title'](),
    ),
    trustBody: pick(copy.trustBody, () =>
      video ? m['directory.ai_tools.photo_video.trust_body']() : m['directory.ai_tools.photo.trust_body'](),
    ),
    galleryTitle: pick(copy.galleryTitle, () =>
      video ? m['directory.ai_tools.photo_video.gallery_title']() : m['directory.ai_tools.photo.gallery_title'](),
    ),
    galleryDescription: pick(copy.galleryDescription, () =>
      video
        ? m['directory.ai_tools.photo_video.gallery_description']()
        : m['directory.ai_tools.photo.gallery_description'](),
    ),
    stylesTitle: pick(copy.stylesTitle, () =>
      video ? m['directory.ai_tools.photo_video.styles_title']() : m['directory.ai_tools.photo.styles_title'](),
    ),
    ideasTitle: pick(copy.ideasTitle, () => m['directory.ai_tools.photo.ideas_title']()),
    ctaDescription: pick(copy.ctaDescription, () =>
      video ? m['directory.ai_tools.photo_video.cta_description']() : m['directory.ai_tools.photo.cta_description'](),
    ),
    ctaButton: pick(copy.ctaButton, () =>
      video ? m['directory.ai_tools.photo_video.cta_button']() : m['directory.ai_tools.photo.cta_button'](),
    ),
  };
}
